// src/services/dashboardService.ts
import { apiService } from './api';
import { socketService } from './socketService';
import type {
  Dashboard,
  DashboardGroupConversations,
  Layer,
  UserProfile,
  PersonData,
  ProjectInfo,
  SideMenuProject,
  RequestGroupConversations,
  TotalItemsByColumn,
  BetaInfo,
  AlertSideMenu,
  AlertNotification,
  RequestConversationMoveDrag,
  SideMenu,
  DashboardRequest,
  ItemForGetPeopleListToSP,
  LayerMicroSP,
  DashboardGroupVM,
  DuplicateRequest,
  ImportPayload,
} from '../types/dashboard';

class DashboardService
{
  /**
   * Obtiene las conversaciones iniciales del dashboard con sus grupos
   */
  async getInitialDashboardGroupConversations(
    requestGroupConversation: RequestGroupConversations,
  ): Promise<DashboardGroupConversations[]>
  {
    try
    {
      //return [];
      const response = await apiService.post<DashboardGroupConversations[]>(
        '/conversation/GetInitialDashboardGroupConversations',
        requestGroupConversation,
        { timeout: 120000 },
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting initial dashboard conversations:', error);
      throw error;
    }
  }

  /**
   * Obtiene la cantidad de conversaciones del dashboard con sus grupos
   */
  async getTotalItemsByColumn(
    requestGroupConversation: RequestGroupConversations,
  ): Promise<TotalItemsByColumn[]>
  {
    try
    {
      //return [];
      const response = await apiService.post<TotalItemsByColumn[]>(
        '/conversation/GetTotalItemsByColumn',
        requestGroupConversation,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting total items by column:', error);
      throw error;
    }
  }

  /**
   * Obtiene el perfil del usuario
   */
  async getUserProfile(): Promise<UserProfile>
  {
    try
    {
      const response = await apiService.get<UserProfile>('/profile/Get');
      return response;
    }
    catch (error)
    {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  /**
   * Obtiene los datos de la persona autenticada
   */
  async getPeopleData(): Promise<PersonData>
  {
    try
    {
      const response = await apiService.get<PersonData>('/Person/GetPeopleData/');
      return response;
    }
    catch (error)
    {
      console.error('Error getting people data:', error);
      throw error;
    }
  }

  /**
   * Obtiene las capas (layers) disponibles para el usuario
   */
  async getPersonLayers(): Promise<Layer[]>
  {
    try
    {
      const response = await apiService.get<Layer[]>('/layer/getpersonlayers/');
      if (response)
      {
        sessionStorage.setItem('C2_AllLayers', JSON.stringify(response));
      }
      return response;
    }
    catch (error)
    {
      console.error('Error getting person layers:', error);
      throw error;
    }
  }

  /**
   * Obtiene información del proyecto
   */
  async getProject(projectId: number): Promise<ProjectInfo>
  {
    try
    {
      const response = await apiService.get<ProjectInfo>(
        `/project/GetProject?projectId=${projectId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting project:', error);
      throw error;
    }
  }

  /**
   * Verifica si el usuario tiene permisos en el proyecto
   */
  async userHasPermissionProject(projectId: number): Promise<boolean>
  {
    try
    {
      const response = await apiService.get<boolean>(
        `/project/UserHasPermissionProject?ProjectId=${projectId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error checking project permission:', error);
      throw error;
    }
  }

  /**
   * Obtiene los proyectos del menú lateral
   */
  async getSideMenuProjects(
    configValueId: number,
  ): Promise<{ TotalCount: number; Projects: SideMenuProject[] }>
  {
    try
    {
      const response = await apiService.post<{ TotalCount: number; Projects: SideMenuProject[] }>(
        '/Project/GetSideMenuProjects',
        {
          ConfigValueId: configValueId,
          Filters: [],
          GroupByDetailId: 0,
          GroupByLayerId: 0,
          GroupByValueField: '',
          IsFromProject: false,
          ProjectName: '',
        },
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting side menu projects:', error);
      throw error;
    }
  }

  /**
   * Obtiene los dashboards por ConfigValueId
   */
  async getDashboardsByConfigValue(
    configValueId: number,
    projectId?: number,
    organizeDashboard?: boolean,
  ): Promise<Dashboard[]>
  {
    try
    {
      const response = await apiService.get<Dashboard[]>(
        projectId !== -1
          ? `/dashboard/getbyconfigvalueid?configValueId=${configValueId}&projectId=${projectId}`
          : organizeDashboard
            ? '/dashboard/GetDashboadsGTD'
            : `/dashboard/getbyconfigvalueid?configValueId=${configValueId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting dashboards:', error);
      throw error;
    }
  }

  /**
   * Obtiene los mensajes no leídos
   */
  async getMessagesUnread(): Promise<number>
  {
    try
    {
      const response = await apiService.get<number>('/Person/GetMessagesUnread');
      return response;
    }
    catch (error)
    {
      console.error('Error getting unread messages:', error);
      return 0;
    }
  }

  /**
   * Obtiene información de beta (brandName, brandImage,isEnableCustomBranding)
   */
  async getIsBeta(): Promise<BetaInfo>
  {
    try
    {
      const response = await apiService.get<BetaInfo>('/Parameter/GetIsBeta/');
      return response;
    }
    catch (error)
    {
      console.error('Error getting beta info:', error);
      throw error;
    }
  }

  /**
   * Get Total of Notifications
   */
  async getAlertSideMenu(): Promise<AlertSideMenu[]>
  {
    try
    {
      const response = await apiService.get<AlertSideMenu[]>('/alert/GetTotalAlertsByPerson/');
      return response;
    }
    catch (error)
    {
      console.error('Error getting beta info:', error);
      throw error;
    }
  }

  async getTotalAlertsByPerson(): Promise<AlertSideMenu[]>
  {
    try
    {
      const response = await apiService.get<AlertSideMenu[]>('alert/GetTotalAlertsByPerson');
      return response;
    }
    catch (error)
    {
      console.error('Error getting alert totals:', error);
      throw error;
    }
  }

  async getUnreadAlertConversationIds(): Promise<Set<number>>
  {
    try
    {
      const tz = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      const data = await apiService.get<AlertNotification[]>(
        `alert/GetAllAlertsByPage?configValueId=0&Page=1&PageSize=500&OnlyRead=1&timeZone=${tz}&textSearch=&important=0`,
      );
      return new Set(data.filter(a => !a.Viewed && a.ConversationId).map(a => a.ConversationId));
    }
    catch
    {
      return new Set();
    }
  }

  /**
   * Changes Title of Project
   */
  async updateconversationtitle(
    Title: string,
    ConfigValueID: number,
    ConversationId: number,
    SocketConnectionId?: string,
  ): Promise<any>
  {
    try
    {
      const response = await apiService.put('/conversation/updateconversationtitle/', {
        ConversationId,
        Title,
        ConfigValueID,
        SocketConnectionId: SocketConnectionId || socketService.connectionId,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error updating project title:', error);
      throw error;
    }
  }
  /*
   * Obtiene los filtros del dashboard
   */
  async getDashboardGlobalFilters(dashboardId: number = 0): Promise<string>
  {
    try
    {
      const response = await apiService.get<string>(
        `/dashboard/GetDashboardGlobalFilters?dashboardId=${dashboardId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting filters of dashboard:', error);
      throw error;
    }
  }

  /**
   * Guarda las conversaciones despues de moverlas por drag and drop
   */
  async InsertDashboardGroupMultipleConversations(
    request: RequestConversationMoveDrag,
  ): Promise<any[]>
  {
    try
    {
      const endpoint = `/conversation/InsertDashboardGroupMultipleConversations?DashboardId=${request.dashboardId}&DashboardGroupId=${request.dashboardGroupId}&ConversationIds=${request.conversationId}&ParentConversationId=${request.pTmp}&DashboardGroupIdOrigin=${request.pDashboardGroupId}&RemovedFromParent=${request.removedFromParent}&UpdateParent=${request.updateParent}&PreviousParentId=${request.pDashboardGroupId}&pParentId=${request.pParentId}&pDashboardId=${request.pDashboardId}&pDashboardGroupId=${request.pDashboardGroupId}&iConversationId=${request.iConversationId}&nDashboardId=${request.nDashboardId}&nDashboardGroupId=${request.nDashboardGroupId}&nParentId=${request.nParentId}&socketConnectionId=${request.socket}`;

      const response = await apiService.post<any[]>(endpoint);
      return response || [];
    }
    catch (error)
    {
      console.error('Error inserting dashboard:', error);
      throw error;
    }
  }

  /**
   * Get Side Menu
   */
  async GetByPerson(): Promise<SideMenu[]>
  {
    try
    {
      const response = await apiService.get<SideMenu[]>(`/Menu/GetByPerson`);
      return response;
    }
    catch (error)
    {
      console.error('Error getting side menu items:', error);
      throw error;
    }
  }

  /**
   * Add Dashboard
   */
  async AddDashboard(request: DashboardRequest): Promise<Dashboard>
  {
    try
    {
      const response = await apiService.post<Dashboard>(`/dashboard`, request);
      return response;
    }
    catch (error)
    {
      console.error('Error getting side menu items:', error);
      throw error;
    }
  }

  /**
   * Update Dashboard
   */
  async UpdateDashboard(request: Dashboard): Promise<Dashboard>
  {
    try
    {
      const response = await apiService.put<Dashboard>(`/dashboard/updatedashboard`, {
        ...request,
        SocketConnectionId: socketService.connectionId,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error getting side menu items:', error);
      throw error;
    }
  }

  /**
   * Update Dashboard
   */
  async UpdateDashboardHistorical(request: any): Promise<void>
  {
    try
    {
      await apiService.put<Dashboard>(`/dashboard/updatedashboardhistorical`, request);
    }
    catch (error)
    {
      console.error('Error getting side menu items:', error);
      throw error;
    }
  }

  /**
   * Delete Dashboard
   */
  async DeleteDashboard(dashboardId: number): Promise<void>
  {
    try
    {
      await apiService.get<Dashboard>(
        `/dashboard/Delete?id=${dashboardId}&SocketConnectionId=${socketService.connectionId}`,
      );
    }
    catch (error)
    {
      console.error('Error getting side menu items:', error);
      throw error;
    }
  }

  /**
   * Get List People By ProjectId
   */
  async GetListPeopleByProjectId(
    projectId: number | null | undefined,
    dashboardId: number | undefined,
  ): Promise<ItemForGetPeopleListToSP[]>
  {
    try
    {
      const response = await apiService.get<ItemForGetPeopleListToSP[]>(
        `/Person/GetListPeopleByProjectId?projectId=${projectId}&dashboardId=${dashboardId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting list:', error);
      throw error;
    }
  }

  /**
   * Save Person SP Project
   */
  async SavePersonSPProject(request: ItemForGetPeopleListToSP): Promise<boolean>
  {
    try
    {
      const response = await apiService.post<boolean>(`/Person/SavePersonSPProject`, request);
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Return Report Dashboard Efficiency
   */
  async ReportDashboardEfficiency(request: {
    DashboardId: number;
    StartDate: string;
    DueDate: string;
    Today: string;
    Name: string;
    WebUrl: string;
  }): Promise<Blob>
  {
    try
    {
      const response = await apiService.post<Blob>(
        `/Notification/ReportDashboardEfficiency`,
        request,
        { responseType: 'blob' },
      );
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Get Layers By ProjectId
   */
  async GetLayersByProjectId(projectId: string): Promise<LayerMicroSP>
  {
    try
    {
      const response = await apiService.get<LayerMicroSP>(
        `/Layer/GetLayersByProjectId?projectId=${projectId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Save Project
   */
  async AddColumn(requestConfig: {
    Conversations: never[];
    DashboardId: number;
    Name: any;
    SocketConnectionId: string;
    TotalItems: number;
    isNew: boolean;
    col: number;
    positionX: number;
    row: number;
    sizeX: number;
    sizeY: number;
  }): Promise<DashboardGroupVM>
  {
    try
    {
      const response = await apiService.post<DashboardGroupVM>(`/dashboardgroup`, requestConfig);
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Duplicate Complete Dashboard
   */
  async DuplicateCompleteDashboard(requestConfig: DuplicateRequest): Promise<Dashboard>
  {
    try
    {
      const response = await apiService.post<Dashboard>(
        `/dashboard/duplicateCompleteDashboard`,
        requestConfig,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Change Conversations Project
   */
  async ChangeConversationsProject(requestConfig: {
    IsPreview: number;
    ProjectId?: number;
    ConversationsIds: string;
  })
  {
    try
    {
      const response = await apiService.post(
        `/Conversation/ChangeConversationsProject`,
        requestConfig,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Duplicate Dashboard Project
   */
  async DuplicateDashboardProject(requestConfig: {
    IsPreview: number;
    ProjectId: number;
    DashboardId: number;
    ConversationsIds: string;
    SocketConnectionId: string;
    DuplicateConversationDetails: {
      ConversationId: number;
      DashboardGroupId: number;
      DashboardGroupConversationId: number;
      IsParent: boolean;
      IsArchived: boolean;
      Progress: number;
    }[];
  })
  {
    try
    {
      const response = await apiService.post(
        `/Conversation/DuplicateDashboardProject`,
        requestConfig,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Add Pined Dashboard (marks a dashboard as pinned/favourite for the user)
   */
  async AddPinedDashboard(dashboardId: number, order: number)
  {
    try
    {
      const response = await apiService.get(
        `/dashboard/AddPinedDashboard?DashboarId=${dashboardId}&order=${order}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error adding pined dashboard:', error);
      throw error;
    }
  }

  /**
   * Delete Pined Dashboard (removes the dashboard pin/favourite for the user)
   */
  async DeletePinedDashboard(dashboardId: number)
  {
    try
    {
      const response = await apiService.get(`/dashboard/DeletePinedDashboard?Id=${dashboardId}`);
      return response;
    }
    catch (error)
    {
      console.error('Error deleting pined dashboard:', error);
      throw error;
    }
  }

  /**
   * Delete Pined Tab Dashboard
   */
  async DeletePinedTabDashboard(
    configValueId: number,
    id: number,
    projectId: number,
    socketConnectionId: string,
  )
  {
    try
    {
      const response = await apiService.get(
        `/dashboard/DeletePinedTabDashboard?ConfigValueId=${configValueId}&Id=${id}&ProjectId=${projectId}&socketConnectionId=${socketConnectionId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Add Pined Tab Dashboard
   */
  async AddPinedTabDashboard(
    dashboardId: number,
    order: number,
    configValueId: number,
    projectId: number,
    UpdateOrder: boolean,
    socketConnectionId: string,
  )
  {
    try
    {
      const response = await apiService.get(
        `/dashboard/addPinedTabDashboard?ConfigValueId=${configValueId}&DashboarId=${dashboardId}&ProjectId=${projectId}&UpdateOrder=${UpdateOrder}&order=${order}&socketConnectionId=${socketConnectionId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Import Cards From Jira
   */
  async ImportCardsFromJira(request: ImportPayload)
  {
    try
    {
      const response = await apiService.post(`/tool/jiraimportcards`, request);
      return response;
    }
    catch (error)
    {
      console.error('Error setting sp:', error);
      throw error;
    }
  }

  /**
   * Update conversation body (project description)
   */
  async updateConversationBody(request: {
    Body: string;
    ConversationId: number;
    ConfigValueId: number;
    SocketConnectionId: string;
  }): Promise<any>
  {
    try
    {
      const response = await apiService.put('/conversation/updateconversationbody', request);
      return response;
    }
    catch (error)
    {
      console.error('Error updating conversation body:', error);
      throw error;
    }
  }

  /**
   * Upload project image
   */
  async uploadProjectImage(formData: FormData): Promise<{ CustomImageId: number; ThumbnailPath: string } | null>
  {
    try
    {
      const response = await apiService.post<{ CustomImageId: number; ThumbnailPath: string }>(
        '/customfieldvalue/uploadimage',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response;
    }
    catch (error)
    {
      console.error('Error uploading project image:', error);
      throw error;
    }
  }

  /**
   * Duplicate Project
   */
  async DuplicateProject(formData: any)
  {
    try
    {
      const response = await apiService.post('/conversation/duplicateproject', formData);
      return response;
    }
    catch (error)
    {
      console.error('Error uploading project image:', error);
      throw error;
    }
  }

  /**
   * Check if conversations are duplicated in a target dashboard (used before drag-to-tab)
   */
  async getConversationsDuplicated(
    dashboardId: number,
    dashboardGroupId: number | null,
    conversationIds: string,
  ): Promise<boolean>
  {
    try
    {
      const response = await apiService.get<boolean>(
        `/dashboard/GetConversationsDuplicated?dashboardId=${dashboardId}&dashboardGroupId=${dashboardGroupId}&conversationIds=${conversationIds}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error checking conversation duplicates:', error);
      return false;
    }
  }

  /**
   * Move conversations to a different dashboard via drag-to-tab
   */
  async addDashboardGroupByParametersDrag(params: {
    dashboardIdSendTo: number;
    conversationIds: string;
    removeDuplicates: boolean;
    moveAll: boolean;
    dashboardIdCurrent: number;
    isFromMoveTo: null;
    ParentConversationId: number;
    dashboardGroupOrigin: number;
    socketConnectionId: string;
  }): Promise<any>
  {
    try
    {
      const qs = `dashboardIdSendTo=${params.dashboardIdSendTo}&conversationIds=${params.conversationIds}&removeDuplicates=${params.removeDuplicates}&moveAll=${params.moveAll}&dashboardIdCurrent=${params.dashboardIdCurrent}&isFromMoveTo=${params.isFromMoveTo}&ParentConversationId=${params.ParentConversationId}&dashboardGroupOrigin=${params.dashboardGroupOrigin}&socketConnectionId=${params.socketConnectionId}`;
      const response = await apiService.get<any>(
        `/dashboard/adddashboardgroupbyparametersdrag?${qs}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error adding dashboard group by drag:', error);
      throw error;
    }
  }

  /**
   * Delete a conversation from a dashboard group (used after drag-to-tab to remove from source)
   */
  async deleteDashboardGroupConversation(params: {
    DashboardGroupId: number;
    ConversationId: number;
    IsFromBookmark: boolean;
    DashboardGroupIdDefault?: number;
    pParentId?: number;
    pDashboardGroupId?: number;
    layersToHide: number;
    socketConnectionId: string;
  }): Promise<any[]>
  {
    try
    {
      const qs = `DashboardGroupId=${params.DashboardGroupId}&ConversationId=${params.ConversationId}&IsFromBookmark=${params.IsFromBookmark}&DashboardGroupIdDefault=${params.DashboardGroupIdDefault ?? 'undefined'}&pParentId=${params.pParentId ?? 'undefined'}&pDashboardGroupId=${params.pDashboardGroupId ?? 'undefined'}&layersToHide=${params.layersToHide}&socketConnectionId=${params.socketConnectionId}`;
      const response = await apiService.post<any[]>(
        `/conversation/DeleteDashboardGroupConversation?${qs}`,
      );
      return response || [];
    }
    catch (error)
    {
      console.error('Error deleting dashboard group conversation:', error);
      throw error;
    }
  }

  /**
   * Update progress percentages in a dashboard group after cards are moved
   */
  async updatePercentDashboardGroupConversation(dashboardGroupId: number): Promise<any[]>
  {
    try
    {
      const response = await apiService.post<any[]>(
        `/conversation/UpdatePercentDashboardGroupConversation?DashboardGroupId=${dashboardGroupId}`,
      );
      return response || [];
    }
    catch (error)
    {
      console.error('Error updating percent dashboard group conversation:', error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService();
